import React, { useState } from 'react';
import { useT } from '../context/LangContext';

/**
 * PartnerLockup — AGRA mark + the official "In Partnership with mastercard foundation"
 * lock-up as a single supplied asset.
 *
 * Asset expectations (drop into /app/frontend/public/branding/):
 *   - in-partnership-lockup.png            (full-colour, for light/cream backgrounds)
 *   - in-partnership-lockup-reversed.png   (white text + MCF symbol in trademark colour,
 *                                           for dark/photographic backgrounds)
 *
 * If those files are not yet on disk the component gracefully falls back to the
 * previous standalone Mastercard Foundation mark — visually less ideal, but
 * never broken.
 *
 * The supplied asset is used EXACTLY as delivered — never recoloured, redrawn,
 * cropped, distorted, or rotated. Aspect ratio is preserved. Clear space is
 * enforced via padding.
 *
 * Variants:
 *   "default"  — light/cream backgrounds (header, programme-context section).
 *   "dark"     — dark/photographic backgrounds (hero header, footer).
 *
 * Sizes (height of the lock-up image):
 *   "sm"  ≈ 32 px (compact header lockup)
 *   "md"  ≈ 44 px (programme context, footer)
 *   "lg"  ≈ 56 px (display use)
 */
export default function PartnerLockup({ variant = 'default', size = 'sm' }) {
  const t = useT();
  const dark = variant === 'dark';

  const agraSrc = '/branding/agra-logo.png';
  const lockupSrc = dark
    ? '/branding/in-partnership-lockup-reversed.png'
    : '/branding/in-partnership-lockup.png';
  const lockupFallback = '/branding/mastercard-foundation.png';

  const [usingFallback, setUsingFallback] = useState(false);

  const heightClass =
    size === 'lg' ? 'h-24' : size === 'md' ? 'h-20' : 'h-14';
  const agraHeight =
    size === 'lg' ? 'h-16' : size === 'md' ? 'h-14' : 'h-10';
  const dividerColor = dark ? 'bg-white/25' : 'bg-[#07743F]/15';

  return (
    <div
      className="flex items-center gap-4"
      data-testid={'partner-lockup-' + variant}
      aria-label={t('partner.lockup_aria', 'AGRA in partnership with Mastercard Foundation')}
    >
      {/* AGRA logo — never altered. White-reverse via CSS filter on dark variant. */}
      <img
        src={agraSrc}
        alt="AGRA"
        loading="lazy"
        className={agraHeight + ' w-auto select-none ' + (dark ? 'brightness-0 invert' : '')}
        draggable="false"
      />

      <span className={'w-px self-stretch ' + dividerColor} aria-hidden="true" />

      {usingFallback ? (
        /* Graceful fallback: text + small MCF mark, until the official lock-up
           PNG is supplied. Preserves brand trademark colours. */
        <div className="flex items-center gap-3">
          <span
            className={
              'kicker leading-tight whitespace-nowrap ' +
              (dark ? 'text-white/85' : 'text-[#58595B]')
            }
            style={{ fontSize: size === 'lg' ? 12 : 10.5, letterSpacing: '0.18em' }}
          >
            {t('partner.in_partnership', 'In Partnership with')}<br />
            {t('partner.mastercard_foundation', 'Mastercard Foundation')}
          </span>
          <img
            src={lockupFallback}
            alt="Mastercard Foundation"
            loading="lazy"
            className={heightClass + ' w-auto select-none'}
            draggable="false"
          />
        </div>
      ) : (
        /* Official single-image "In Partnership with mastercard foundation" lockup.
           Sized by height only — width is intrinsic so proportions are preserved. */
        <img
          src={lockupSrc}
          alt={t('partner.lockup_alt', 'In partnership with Mastercard Foundation')}
          loading="lazy"
          onError={() => setUsingFallback(true)}
          className={heightClass + ' w-auto select-none'}
          draggable="false"
          data-testid="partner-lockup-image"
        />
      )}
    </div>
  );
}
